/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { PERMISSION_GROUPS } from '../utils/permission-groups';
import { getInitiativeGroupTree } from './initiativeTree';
import { findRootParentInTree } from '../utils/initiative';

export const currentPortfolio = (state: RootState) => state.portfolio.loaded ? state.portfolio.data : undefined;
const portfolioTree = (state: RootState) => state.portfolioTree.loaded ? state.portfolioTree.data : undefined;

export const getUserPortfolioPermissionTags = createSelector([currentPortfolio], (portfolio) => {
  if (portfolio && portfolio.userPermissions) {
    return portfolio.userPermissions.permissions;
  }
  return [];
});

export const getPortfolioUsage = createSelector([currentPortfolio], (portfolio) => {
  return portfolio ? portfolio.usage : [];
});

const getPortfolioPermissionGroup = createSelector(
  [
    portfolioTree,
    currentPortfolio
  ], (rootInitiatives, initiative) => {

    const defaultPermissionGroup = PERMISSION_GROUPS.PORTFOLIO_TRACKER;

    const initiativeId = initiative?._id;
    if (!initiativeId) {
      return defaultPermissionGroup;
    }

    if (initiative.permissionGroup && initiative.permissionGroup !== PERMISSION_GROUPS.FREE) {
      return initiative.permissionGroup;
    }

    function getParentPermissionGroup(parentId: string | undefined): string {
      if (!parentId) {
        return defaultPermissionGroup;
      }
      const initiative = rootInitiatives?.find(d => d._id === parentId);
      if (!initiative) {
        return defaultPermissionGroup;
      }
      if (initiative.permissionGroup && initiative.permissionGroup !== PERMISSION_GROUPS.FREE) {
        return initiative.permissionGroup;
      }
      if (initiative.parentId) {
        return getParentPermissionGroup(initiative.parentId);
      }
      return defaultPermissionGroup;
    }

    return getParentPermissionGroup(initiative.parentId);
  });

export const isPortfolioTrackerExchange = createSelector([getPortfolioPermissionGroup], (permissionGroup) => {
  return permissionGroup === PERMISSION_GROUPS.PORTFOLIO_TRACKER_EXCHANGE
});

export const getCurrentPortfolioTree = createSelector([getInitiativeGroupTree, currentPortfolio], (tree, portfolio) => {
  if (!portfolio) {
    return tree;
  }

  const root = findRootParentInTree(tree, portfolio?._id)
  return { ...tree, children: tree.children.filter(({ id }) => id === root?.id) };
});
